import { computed, type MaybeRef, unref } from "vue";
import { useInfiniteQuery } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";
import type { ExpertLevelAssignment } from "@shopexperts/legacy-api";

interface UseAdminExpertLevelHistoryOptions {
  perPage?: number;
  enabled?: MaybeRef<boolean>;
}

function isPositiveId(value: number | null | undefined): value is number {
  return typeof value === "number" && value > 0;
}

export function useAdminExpertLevelHistory(
  expertId: MaybeRef<number | null | undefined>,
  options: UseAdminExpertLevelHistoryOptions = {},
) {
  const api = useApi();
  const perPage = options.perPage ?? 20;
  const resolvedExpertId = computed(() => unref(expertId));
  const enabled = computed(() => (unref(options.enabled) ?? true) && isPositiveId(resolvedExpertId.value));

  const query = useInfiniteQuery({
    queryKey: computed(() => [
      "admin",
      "expert-levels",
      "history",
      resolvedExpertId.value,
      { per_page: perPage },
    ]),
    queryFn: async ({ pageParam }) => {
      if (!isPositiveId(resolvedExpertId.value)) {
        throw new Error("expertId is required to load expert level history");
      }

      return await api.admin.expertLevels.getExpertLevelHistory({
        params: { expert: resolvedExpertId.value },
        query: {
          per_page: perPage,
          cursor: pageParam,
        },
      });
    },
    getNextPageParam: (lastPage) => lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined,
    initialPageParam: undefined as string | undefined,
    enabled,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });

  const assignments = computed<ExpertLevelAssignment[]>(() =>
    query.data.value?.pages.flatMap((page) => page.data) ?? [],
  );

  return {
    assignments,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    query,
  };
}
